import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { cylinderSearchActions, sliceName } from './store/slices/cylinderSearchSlice';
import { useCallback, useMemo, useState } from 'react';
import { ObjectTypes, SortDirection } from '../../utils/constants';
import { doSearchStoreActionHandler, loadGridColumnSettingStoreActionHandler, loadSavedFilterStoreActionHandler } from './store/actions/cylinderSearchActions';
import SearchPage from '../../components/SearchPage/SearchPage';
import ColumnActionFormatter, { CheckboxSelectionFormatter } from '../../components/Table/ColumnActionFormatter/ColumnActionFormatter';
import toast from 'react-hot-toast';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Checkbox, Chip, Link, TableCell } from '@mui/material';
import cylinderApi from '../../services/api/CylinderApi';
import { downloadBlobFile } from '../../utils/fileUtils';
import OrderApi from '../../services/api/OrderApi';
import useActionCreation from '../../hooks/useActionCreation';
import FeedbackFormDialog from '../../components/Dialogs/FeedbackFormDialog/FeedbackFormDialog';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';

const sortColumnList = [
  { value: 'JobName' },
  { value: 'CreatedOn' },
  { value: 'DateReceived' },
];

const statusColors = {
  Scheduled: 'primary',
  Hold: 'secondary',
  'Partially Shipped': 'warning',
  Complete: 'success',
  Cancelled: 'error',
  Returned: 'success',
  Shipped: 'primary',
};

const CylinderSearch = () => {
  const [openFeedbackComment, setopenFeedbackComment] = useState(false);
  const [cylinderId, setCylinderId] = useState();
  const [cylinderNameFeedback, setCylinderNameFeedback] = useState();
  const [selectedCylinderIDs, setSelectedCylinderIDs] = useState({});
  const [isSelectAllChecked, setIsAllSelected] = useState(false);
  const [rowsDownloadStatus, setRowsDownloadStatus] = useState({});
  const { t } = useTranslation();
  const { dataRowItems, tableInfo, filter } = useSelector((state) => state.cylinderSearch);

  const breadcrumbsLinks = useMemo(
    () => [{ to: '/', text: t('Nav_Menu_Home') }, { text: t('Nav_Menu_Cylinders') }],
    [t]
  );
  const onSelectionHandler = useCallback((event, row) => {
    const { OrderIDText } = row;
    const updatedSelectedIds = { ...selectedCylinderIDs, [OrderIDText]: event?.target?.checked };
    setSelectedCylinderIDs(updatedSelectedIds);

    // checking length of all selcted rows
    const selectedItems = Object.keys(updatedSelectedIds)?.length || 0;

    // checking if every row is selected
    const isAllItemsSelected = Object.values(updatedSelectedIds).every((s) => !!s);

    // if every row is selected and row count is equal to datarows count , automatically check the selectall checkbox as true
    if (selectedItems === dataRowItems?.length && isAllItemsSelected) {
      setIsAllSelected(true);
    } else {
      setIsAllSelected(false);
    }
  }, [dataRowItems?.length, selectedCylinderIDs]);

  const onSelectAllHandler = useCallback((event) => {
    const { checked = false } = event?.target || {};
    setIsAllSelected(checked);
    const updateddataItems = {};
    dataRowItems.forEach((data) => {
      updateddataItems[data?.OrderIDText] = checked;
    });
    setSelectedCylinderIDs(updateddataItems);
  }, [dataRowItems]);

  const handleDownload = async (OrderIDText, FileName, documentType) => {
    setRowsDownloadStatus({ ...rowsDownloadStatus, [OrderIDText]: 'Pending' });
    try {
      const response = await OrderApi.downloadReport(OrderIDText, documentType);
      if (response) {
        const blob = new Blob([response], { type: 'application/pdf' });
        downloadBlobFile(FileName, blob);
        setRowsDownloadStatus({ ...rowsDownloadStatus, [OrderIDText]: 'Done' });
        toast.success(t('Toast_FileDownloadedSuccessfully'));
      }
    } catch (error) {
      setRowsDownloadStatus({ ...rowsDownloadStatus, [OrderIDText]: 'Done' });
      toast.error(t('Error_Message_SomethingWentWrong'));
      console.error('Error downloading file:', error);
    }
  };
  const { buttonLinkBuilder, actionDialogComponent } = useActionCreation({
    dialogTitleKey: 'Dialog_Title_NewActionForCylinder',
    belongsTo: 'Cylinders',
    sourceLinkCallback: ({ id }) => `/Cylinders/${id}`,
  });

  const addFeedback = useCallback(({ CylinderID, CylinderMovementID }) => () => {
    setopenFeedbackComment(true);
    setCylinderId(String(CylinderMovementID));
    setCylinderNameFeedback(CylinderID);
  }, []);

  const closeFeedbackDialogHandler = () => {
    setopenFeedbackComment(false);
  };

  const cellMetaData = [
    {
      icon: <FeedbackOutlinedIcon />, handler: addFeedback
    }
  ];
  const columnMappingFormatter = {
    Action: ({ row }) => (
      <>
        <ColumnActionFormatter cellMetaData={cellMetaData} rowItem={row} />
        {buttonLinkBuilder({
          id: String(row?.CylinderMovementID),
          text: row?.CylinderMovementID,
        })}
      </>
    ),
    Selection: ({ row }) => (
      <CheckboxSelectionFormatter
        isSelectAllChecked={isSelectAllChecked}
        objectId={row?.OrderIDText}
        selectedObjectIDs={selectedCylinderIDs}
        rowItem={row}
        handler={onSelectionHandler}
      />
    ),
    StatusName: ({ row, field }) => {
      const statusValue = row?.[field];
      if (!statusValue) return '';
      const labelColor = statusColors[statusValue];
      return (
        <Chip
          label={statusValue}
          color={labelColor}
          sx={{ minWidth: 90 }}
          size="small"
          variant="filled"
        />
      );
    },
    OrderNumber: ({ row, field }) => (
      <Link
        color="primary"
        component={RouterLink}
        to={`/Orders/${row.OrderIDText}/view`}
        underline="none"
        variant="subtitle2"
      >
        {row[field]}
      </Link>),
    RentalAgreementNumber: ({ row }) => (row?.AttachedFiles?.length > 0 ? (
      <>
        {/* {
      row?.AttachedFiles?.map((file) => (
        <Box key={file.id} sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={row.RentalAgreementNumber}>
            <IconButton size="small" onClick={() => handleDownload(row.OrderIDText, file.FileName, file.DocumentType)}>
              <Article color="error" />
            </IconButton>
          </Tooltip>
        </Box>
      ))
    } */}
        {
      row?.AttachedFiles?.map((file) => (
        <Box key={file.id} sx={{ display: 'flex', alignItems: 'center' }}>
          <Link
            component="button"
            onClick={() => handleDownload(row.OrderIDText, file.FileName, file.DocumentType)}
            sx={{ cursor: 'pointer', textDecoration: 'underline', border: 'none', background: 'none', padding: 0, color: 'primary.main' }}
          >
            {row.RentalAgreementNumber}
          </Link>
        </Box>
      ))
    }

      </>
    ) : null),

  };
  const columnHeaderMappingFormatter = useMemo(() => ({ key: 'Selection',
    renderer: (
      <TableCell
        key="tableCol-Selection"
        sx={{
          whiteSpace: 'nowrap',
        }}
      >
        <Checkbox
          sx={{
            '& .MuiSvgIcon-root': { fontSize: 24 },
            padding: 0,
          }}
          onChange={onSelectAllHandler}
          checked={isSelectAllChecked}
        />
      </TableCell>
    ) }), [isSelectAllChecked, onSelectAllHandler]);

  const getExportingDataHandler = useCallback(
    async (newPageSize) => {
      try {
        const searchParamData = {
          clientIDs: filter?.clientIDs.map((client) => client.ClientID) ?? [],
          cylinderType: filter?.CylinderType?.id ?? null,
          cylinderStatus: filter?.Status?.id ?? null,
          orderNumber: filter?.OrderNumber ?? '',
          rentalAgreementNumber: filter?.RentalAgreementNumber ?? '',
          cylinderSubStatus: filter?.SubStatus?.id ?? null,
          requestor: filter?.Requestor?.id ?? '',
          reportAddress: filter?.ReportAddress ?? '',
          daysOut: filter?.DaysOut ?? null,
          shippingDateFrom: filter?.ShippingDateFrom ?? null,
          shippingDateTo: filter?.ShippingDateTo ?? null,

          page: tableInfo.page + 1,
          pageSize: newPageSize,
          sortBy: SortDirection.combineSortBy(
            tableInfo.sortColumn,
            tableInfo.sortDirection
          ),
        };

        const data = await cylinderApi.searchCylinder(searchParamData);
        return data.Data;
      } catch (error) {
        toast.error('Something went wrong');
      }
      return [];
    },
    [filter?.clientIDs, filter?.CylinderType?.id, filter?.Status?.id, filter?.OrderNumber, filter?.RentalAgreementNumber, filter?.SubStatus?.id, filter?.Requestor?.id, filter?.ReportAddress, filter?.DaysOut, filter?.ShippingDateFrom, filter?.ShippingDateTo, tableInfo.page, tableInfo.sortColumn, tableInfo.sortDirection]
  );

  return (
    <>
      <SearchPage
        pageTitle={`${t('Tab_Title_AppName')} | ${t('Header_CylinderSearch')}`}
        headerText={t('Header_CylinderSearch')}
        breadcrumbsLinks={breadcrumbsLinks}
        sortColumnList={sortColumnList}
        topRightSectionComponent={null}
        filterSettingObjectType={ObjectTypes.SearchFilter_Cylinders}
        savedFilterObjectType={ObjectTypes.CylinderSearchUserFilter}
        reduxSliceName={sliceName}
        reduxSearchActions={cylinderSearchActions}
        doSearchStoreActionHandler={doSearchStoreActionHandler}
        loadGridColumnSettingStoreActionHandler={loadGridColumnSettingStoreActionHandler}
        loadSavedFilterStoreActionHandler={loadSavedFilterStoreActionHandler}
        getExportingDataHandler={getExportingDataHandler}
        columnSettingObjectType={ObjectTypes.CylinderSearchGrid}
        columnMappingFormatter={columnMappingFormatter}
        exportFilename="Cylinder_List_Exported"
        columnHeaderMappingFormatter={columnHeaderMappingFormatter}
      />
      <FeedbackFormDialog
        isOpen={openFeedbackComment}
        closeDialog={closeFeedbackDialogHandler}
        recordId={cylinderId}
        objectTitle={cylinderNameFeedback}
        belongsTo="CylinderMovement"
      />
      {actionDialogComponent}
    </>
  );
};

export default CylinderSearch;
